// M73a — the "homecoming" close-out: pure math for a stocktake, not a report.
//
// The owner's model, verbatim: "re-inventory the unused boxes — inventory loaded
// mags — account for all rounds used." Fired is DERIVED from what you can count
// on the bench; it is never remembered. Two lanes, disjoint by design:
//
//   BOXED LANE (per manifest line)              MAG LANE (per packed set)
//   ┌──────────────────────────────┐            ┌──────────────────────────┐
//   │ P  packed at pack time       │            │ Ln  contents right now   │
//   │ B  back in boxes    (input)  │            │ Lp  claim at pack (M77c) │
//   │ M  moved into mags  (input)  │──rounds──▶ │ R   left in mags (input) │
//   │ L  lost / duds      (input)  │  entering  │ Fm = base − R            │
//   │ Fb = P − B − M − L           │  the mags  │   base = Ln (whole set)  │
//   └──────────────────────────────┘            │   base = Lp (partial)    │
//        deducts P − B − M                      │ Fm ≤ Ln (LIVE) — always  │
//                                               └──────────────────────────┘
//                                                    deducts Fm
//
// M98 (E1/E10) — THE CEILING. Whatever Fm comes out as, in either mode, it may
// not exceed Ln as it stands RIGHT NOW: `reconcile_session_magazine` refuses
// above SUM(magazine_contents) and the client mirrors exactly that predicate
// through `firedExceedsLoaded`, checked on the DERIVED number at preview and
// at commit (with a fresh Ln), never on the baseline. On the owner's 12 Sep
// trip the baseline was a claim of 44 with 0 loaded: a mirror wired to the
// baseline would have passed and the server would still have refused.
//
// WHY M EXISTS (the bug this phase fixes). M70f let you load mags from packed
// boxes AT the range. The comment in 20260712000000_range_session_magazines.sql
// — "the two never overlap, so no double-count" — was true until that shipped.
// Pack 100, load 20 onsite, fire 15 from mags: the mag lane already deducts
// everything that entered the mags, so the ONLY entry that keeps the books right
// is `returned = 100` (B + M), not the 80 physically sitting in the box. Without
// the M leg the user types 80 and silently books inventory 20 light.
//
// The RPC never learned a new parameter: the client sends `returned = B + M`.
// Everything here is pure so the whole identity is a table test.
//
// NOTE ON HOMES (E3): `rangeReconcile.ts` documents itself as a 1:1 mirror of
// the reconcile_manifest_line RAISE cases. The M split is a CLIENT concept the
// RPC has never heard of, so it does NOT belong there — this module composes
// over that mirror instead of extending it, and the mirror's parity tests keep
// meaning what they claim.

import { validateReconcileLine } from './rangeReconcile';

// ---------------------------------------------------------------------------
// Boxed lane — the M split
// ---------------------------------------------------------------------------

export interface CloseOutLineInput {
  /** P — rounds packed for the trip. */
  packed: number;
  /** B — rounds physically back in boxes. */
  back: number;
  /** M — rounds moved into magazines at the range (0 on an ordinary day). */
  moved: number;
  /** L — lost / duds. */
  lost: number;
  /** The lot's current on-hand (`inventory_ammo.quantity`). */
  onHand: number;
  /** Rounds of THIS lot currently sitting in magazines, all groups (E1). */
  lotInMags?: number;
}

export type CloseOutRefusal = 'split' | 'mirror' | 'choke';

export interface CloseOutValidation {
  ok: boolean;
  /** User-facing message when !ok; empty when ok. */
  error: string;
  /** Which guard refused — the UI offers "Count them" only on `choke`. */
  refusal: CloseOutRefusal | null;
  /** Fb = P − B − M − L. Shown live even when !ok. */
  fired: number;
  /** What the RPC receives: B + M. */
  returned: number;
}

/**
 * Validate one boxed line's split, then delegate to the RPC mirror.
 *
 * With `moved === 0` this is byte-identical to the shipped path: the same
 * mirror, the same messages, the same fired figure (M73a's iron-rule
 * regression). The extra guards only speak up when the seam is actually in
 * play, so an ordinary day never meets new copy.
 */
export function validateCloseOutLine(input: CloseOutLineInput): CloseOutValidation {
  const { packed, back, moved, lost, onHand, lotInMags = 0 } = input;
  const fired = packed - back - moved - lost;
  const returned = back + moved;

  if (back < 0 || moved < 0 || lost < 0) {
    return {
      ok: false,
      error: 'Counts must be zero or more.',
      refusal: 'split',
      fired,
      returned,
    };
  }

  // Own this case when the M leg is in play: the mirror's message names a
  // "brought back" figure the user never typed (it is B + M), which reads as a
  // bug in the app rather than a bug in the entry.
  if (moved > 0 && returned + lost > packed) {
    return {
      ok: false,
      error: `Back in boxes (${back}), into mags (${moved}) and lost (${lost}) add up to more than the ${packed} rounds packed.`,
      refusal: 'split',
      fired,
      returned,
    };
  }

  const mirror = validateReconcileLine({ packed, returned, lost, onHand });
  if (!mirror.ok) {
    return { ok: false, error: mirror.error, refusal: 'mirror', fired, returned };
  }

  // Choke-mirror pre-flight (E7). `guard_inventory_ammo_vs_mags` RAISEs when a
  // lot's quantity drops below the rounds of it sitting in magazines. Closing
  // out deducts P − B − M, so the post-close quantity is onHand − deduct; if
  // that lands under lotInMags the server refuses with "unload them before
  // dropping the count" — a baffling error on a physically correct day. Catch
  // it here and say the useful thing instead. Guarded on lotInMags > 0 so the
  // plain over-draw case keeps the mirror's own wording.
  const deduct = packed - returned;
  if (lotInMags > 0 && deduct > onHand - lotInMags) {
    return {
      ok: false,
      error: `Closing out like this would leave fewer rounds than the ${lotInMags.toLocaleString()} loaded in your magazines — count what went into mags, or unload them first.`,
      refusal: 'choke',
      fired,
      returned,
    };
  }

  return { ok: true, error: '', refusal: null, fired, returned };
}

// ---------------------------------------------------------------------------
// Mag lane — the inversion
// ---------------------------------------------------------------------------

export type MagCountMode = 'fired' | 'left';

/**
 * E6 — "Left in mags" is only meaningful when the pack took the WHOLE group.
 *
 * Magazine groups are fungible (M67): there is no per-mag identity, so when you
 * pack 3 of 5 loaded mags nothing in the system knows which rounds rode along.
 * Counting the bench then and deriving `Ln − R` would treat the mags still in
 * the cabinet as fired — the owner stores loaded mags at home, so that is his
 * steady state, not an edge case. Set equality WAS the whole precondition
 * (bench == set, or the mode is off) until M77c added the pack-time claim,
 * which answers "what rode along" directly for a partial pack — see below.
 */
export function leftInMagsAvailable(
  magsTaken: number,
  loadedCount: number,
  roundsAtPack: number | null = null,
): boolean {
  // M77c — a PARTIAL pack becomes countable once the line carries a pack-time
  // baseline. Set equality was only ever a proxy for "we know what rode along";
  // rounds_at_pack answers that question directly, so the proxy stops being the
  // only way in. Lines without a baseline (legacy, or a pack where the user
  // declined to say) degrade to exactly the pre-M77c gate.
  if (roundsAtPack !== null && roundsAtPack >= 0) return true;
  return magsTaken >= loadedCount && loadedCount > 0;
}

/** E4 (as amended by E6): tally wins, then the stocktake, then fired. */
export function defaultMagMode(tallied: number, canCountLeft: boolean): MagCountMode {
  if (tallied > 0) return 'fired';
  return canCountLeft ? 'left' : 'fired';
}

export interface MagFiredInput {
  mode: MagCountMode;
  /** Raw field text — validated here so the component stays dumb. */
  value: string;
  /** Ln — the group's loaded rounds RIGHT NOW (re-fetched at commit, E2). */
  loadedNow: number;
}

export interface MagFiredResult {
  ok: boolean;
  error: string;
  /** Rounds to send to reconcile_session_magazine. */
  fired: number;
  /** Present in `left` mode: the derived preview line's number. */
  derived: boolean;
}

export function deriveMagFired({ mode, value, loadedNow }: MagFiredInput): MagFiredResult {
  const trimmed = value.trim();
  if (trimmed === '') {
    return { ok: false, error: '', fired: 0, derived: mode === 'left' };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
    return {
      ok: false,
      error: mode === 'left'
        ? 'Rounds left must be a whole number.'
        : 'Rounds fired must be a whole number.',
      fired: 0,
      derived: mode === 'left',
    };
  }
  if (mode === 'fired') {
    return { ok: true, error: '', fired: n, derived: false };
  }
  if (n > loadedNow) {
    return {
      ok: false,
      error: `Your sets show only ${loadedNow.toLocaleString()} rds loaded — log the onsite load first, then count.`,
      fired: 0,
      derived: true,
    };
  }
  return { ok: true, error: '', fired: loadedNow - n, derived: true };
}

// ---------------------------------------------------------------------------
// M98 — the loaded ceiling, the server's refusal, and stale-message clearing
// ---------------------------------------------------------------------------

/**
 * M98 (E1) — the ONE ceiling: the fired number, however it was derived, may
 * not exceed what is loaded in the set RIGHT NOW. Mirrors
 * `reconcile_session_magazine`'s first guard (`p_rounds_fired > SUM(contents)`)
 * one-to-one, so the client can refuse before a round trip and say the same
 * thing the server would — in the app's own words ("set", never "group").
 *
 * Takes the DERIVED fired number and the LIVE loaded total — never the
 * count-down baseline, which on a partial pack is the user's claim.
 *
 * @returns the refusal sentence, or null when the number fits.
 */
export function firedExceedsLoaded(fired: number, loadedLive: number): string | null {
  const live = Math.max(0, Math.floor(loadedLive || 0));
  if (!(fired > live)) return null;
  return `Only ${live.toLocaleString()} rds loaded in this set — can't fire ${fired.toLocaleString()}.`;
}

/**
 * M98 (E2) — is this message the server's loaded-shortfall refusal? A SHAPE
 * test only: it never extracts the numbers (English prose is not an API), and
 * it also matches the client's own sentence so one predicate keys the remedy
 * on both. Callers that match re-derive the sentence from fresh data.
 */
export function isLoadedShortfall(message: string | null | undefined): boolean {
  if (!message) return false;
  return /^Only \d[\d,]* (rounds are|rds) loaded in this (group|set)/.test(message);
}

/**
 * M98 (E5) — which sets' loaded totals actually CHANGED between two reads.
 * The page rebuilds its loaded map on every reload (removing another line,
 * noting a malfunction, saving returns), so an effect keyed on the map object
 * would wipe every line's refusal on any unrelated action. Keying on per-set
 * VALUES means a shown refusal survives everything except the one event that
 * makes it stale: that set's contents moving.
 */
export function changedLoaded(
  prev: Readonly<Record<string, number>>,
  next: Readonly<Record<string, number>>,
): string[] {
  const ids = new Set([...Object.keys(prev), ...Object.keys(next)]);
  const out: string[] = [];
  for (const id of ids) {
    if ((prev[id] ?? 0) !== (next[id] ?? 0)) out.push(id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// The one-tap guard
// ---------------------------------------------------------------------------

export type AllFiredArm = 'mag-lines' | 'in-mags';

export interface AllFiredGuardResult {
  allowed: boolean;
  /** Which arm tripped — drives the caption variant. */
  arm: AllFiredArm | null;
  /** Replaces the button entirely when blocked (E11): a control that can never
   *  be pressed is noise, so it is not rendered disabled. */
  caption: string;
}

/**
 * "All fired" commits `returned = 0` on every line — it deducts everything
 * packed. On a seam day the mag lane ALSO deducts the rounds that entered mags,
 * so the same rounds come off twice.
 *
 * The reconciled arm is load-bearing: close the mag line first with everything
 * fired and `magazine_contents` zeroes out, so an open-lines-only trigger goes
 * false while one tap still double-deducts (lot 150, pack 100, load 20 onsite,
 * fire all → 30 on hand against a truth of 50, silent whenever shelf spares
 * cover the gap).
 */
export function allFiredGuard(input: {
  /** Mag lines on this trip, ANY status — open or reconciled. */
  magLineCount: number;
  /** Open manifest lots that currently have rounds of theirs in magazines. */
  lotsWithInMags: number;
}): AllFiredGuardResult {
  if (input.magLineCount > 0) {
    return {
      allowed: false,
      arm: 'mag-lines',
      caption: 'One-tap unavailable — this trip has magazine lines; count below.',
    };
  }
  if (input.lotsWithInMags > 0) {
    return {
      allowed: false,
      arm: 'in-mags',
      caption: 'One-tap unavailable — some of these rounds are in magazines; count below.',
    };
  }
  return { allowed: true, arm: null, caption: '' };
}

// ---------------------------------------------------------------------------
// The seam offer (nudge → log → pinned)
// ---------------------------------------------------------------------------

export type LoadOffer = 'none' | 'available' | 'warn' | 'no-groups' | 'logged';

export interface SeamState {
  /** Ask whether packed rounds went into mags (never blocks — home loads are a
   *  legitimate reason for M to stay 0). */
  nudge: boolean;
  offer: LoadOffer;
  /** Copy for the `warn` case; empty otherwise. */
  warning: string;
}

/**
 * One line's seam state. `logged` is terminal for the line: once a load has
 * definitely landed, re-offering it is how you double-load, and an inflated
 * `magazine_contents` becomes next trip's phantom deduction (`load_magazine`
 * is not idempotent — E9).
 */
export function loadOfferState(input: {
  packed: number;
  back: number;
  moved: number;
  lost: number;
  lotInMags: number;
  compatibleGroups: number;
  /** The user opened the M disclosure — the nudge has been acknowledged. */
  disclosureOpen: boolean;
  /** "These were loaded at home" — dismissed for this line. */
  nudgeDismissed: boolean;
  /** A load for this line has committed. */
  loadLogged: boolean;
}): SeamState {
  const { packed, back, moved, lost, lotInMags, compatibleGroups } = input;

  if (input.loadLogged) return { nudge: false, offer: 'logged', warning: '' };

  const nudge =
    !input.disclosureOpen &&
    !input.nudgeDismissed &&
    lotInMags > 0 &&
    moved === 0 &&
    back + lost < packed;

  if (moved <= 0) return { nudge, offer: 'none', warning: '' };
  if (compatibleGroups === 0) return { nudge, offer: 'no-groups', warning: '' };

  // Heuristic, and labelled as one: contents are stateless placement, so a
  // pre-trip home load of the same lot looks identical to an onsite load that
  // already got logged. Warn, never remove — the mag lane's R > Ln block sends
  // people here, and suppressing the action would strand them.
  if (lotInMags >= moved) {
    return {
      nudge,
      offer: 'warn',
      warning: 'These rounds may already be logged — did you load them live at the range?',
    };
  }
  return { nudge, offer: 'available', warning: '' };
}

// ---------------------------------------------------------------------------
// The trip balance strip
// ---------------------------------------------------------------------------

export interface StripManifestLine {
  rounds_packed: number;
  rounds_returned: number;
  rounds_lost: number;
  reconciled_at: string | null;
}

export interface StripMagLine {
  rounds_fired: number;
  reconciled_at: string | null;
}

export interface StripLane {
  /** At least one line of this lane has committed. */
  committed: boolean;
  /** Lines still uncounted — the lane's figures describe only what committed. */
  openCount: number;
}

export interface StripTotals {
  /** Hidden until something has committed: an empty strip teaches nothing. */
  visible: boolean;
  /** Every line of both lanes is reconciled — the payoff state. */
  complete: boolean;
  /** The ONE cross-lane figure. Safe by construction: the lanes are disjoint
   *  and both legs are committed. */
  totalFired: number;
  boxed: StripLane & { packed: number; fired: number; unfired: number; lost: number };
  mags: StripLane & { fired: number };
}

/**
 * Totals for the page-level strip — COMMITTED LEGS ONLY (E1).
 *
 * Three numbers that look obvious are deliberately absent. A trip-wide "packed"
 * total would need rounds-at-pack for every magazine line; `rounds_at_pack`
 * exists since M77c but is OPTIONAL and sometimes `~`-estimated, so the sum
 * would be a guess on exactly the days it matters. "Still loaded" would be a live
 * contents read, not a committed leg. And no figure sums across lanes except
 * fired, because rounds that moved boxes → mags exist in the boxed lane's
 * `returned` AND in mag contents; adding them double-counts. M73c revisits all
 * three once loads are trip-scoped.
 */
export function stripTotals(
  manifest: StripManifestLine[],
  magLines: StripMagLine[],
): StripTotals {
  const boxedDone = manifest.filter((l) => !!l.reconciled_at);
  const magsDone = magLines.filter((l) => !!l.reconciled_at);

  const packed = boxedDone.reduce((s, l) => s + l.rounds_packed, 0);
  const unfired = boxedDone.reduce((s, l) => s + l.rounds_returned, 0);
  const lost = boxedDone.reduce((s, l) => s + l.rounds_lost, 0);
  const boxedFired = Math.max(0, packed - unfired - lost);
  const magFired = magsDone.reduce((s, l) => s + l.rounds_fired, 0);

  const totalLines = manifest.length + magLines.length;
  return {
    visible: boxedDone.length + magsDone.length > 0,
    complete:
      totalLines > 0 &&
      boxedDone.length === manifest.length &&
      magsDone.length === magLines.length,
    totalFired: boxedFired + magFired,
    boxed: {
      committed: boxedDone.length > 0,
      openCount: manifest.length - boxedDone.length,
      packed,
      fired: boxedFired,
      unfired,
      lost,
    },
    mags: {
      committed: magsDone.length > 0,
      openCount: magLines.length - magsDone.length,
      fired: magFired,
    },
  };
}


// ---------------------------------------------------------------------------
// M77c — rounds at pack, and what came back
// ---------------------------------------------------------------------------

export interface MagBaselineInput {
  magsTaken: number;
  /** How many mags in the group are loaded right now. */
  loadedCount: number;
  /** The pack-time snapshot, or null when it was never recorded. */
  roundsAtPack: number | null;
  /** Ln — the group's loaded rounds RIGHT NOW (re-fetched at commit, E2). */
  loadedNow: number;
}

export interface MagBaseline {
  /** The number "rounds left" is subtracted FROM. */
  baseline: number;
  /** true → the baseline came from the pack snapshot, not from live contents. */
  fromPack: boolean;
}

/**
 * Which number "Left in mags" counts down from.
 *
 * A WHOLE-GROUP pack keeps the shipped behavior byte-for-byte: bench == group,
 * so live contents ARE the baseline and nothing about that path changes. Only a
 * PARTIAL pack — the case that had no answer before this phase — reaches for
 * the pack snapshot.
 *
 * Split out from `deriveMagFired` deliberately: keeping the decision in its own
 * function is what makes "the whole-group path is untouched" checkable rather
 * than merely claimed.
 */
export function magCloseOutBaseline(input: MagBaselineInput): MagBaseline {
  const { magsTaken, loadedCount, roundsAtPack, loadedNow } = input;
  const wholeGroup = magsTaken >= loadedCount && loadedCount > 0;
  if (!wholeGroup && roundsAtPack !== null && roundsAtPack >= 0) {
    return { baseline: roundsAtPack, fromPack: true };
  }
  return { baseline: loadedNow, fromPack: false };
}

/**
 * DD4 — the one-tap suggestion for a PARTIAL pack: if the group's loaded rounds
 * were spread evenly, this many rode along.
 *
 * Returned as a suggestion, never a prefill. The field starts EMPTY and this
 * only lands when the user taps the chip, because a prefilled guess becomes a
 * confirmed statement the moment the dialog closes — and this number then flows
 * into the ledger, into magazine wear, and into gun round counts.
 */
export function packSuggestion(loadedTotal: number, magsTaken: number, loadedCount: number): number | null {
  if (loadedCount <= 0 || magsTaken <= 0 || loadedTotal <= 0) return null;
  if (magsTaken >= loadedCount) return loadedTotal;   // whole group: exact, not a guess
  return Math.round((loadedTotal * magsTaken) / loadedCount);
}

export interface MagReturnsValidation {
  ok: boolean;
  error: string;
}

/**
 * Mirrors `_validate_mag_returns` one-to-one (E3 doctrine: mag-lane RAISEs live
 * here, not in rangeReconcile). The RPC stays the authority; this is the
 * pre-flight so the sheet can refuse before the server has to.
 */
export function validateMagReturns(
  magsTaken: number,
  magsLoaded: number | null,
  roundsLoaded: number | null,
): MagReturnsValidation {
  if (magsLoaded !== null) {
    if (!Number.isInteger(magsLoaded) || magsLoaded < 0) {
      return { ok: false, error: 'Magazines that came back loaded must be zero or more.' };
    }
    if (magsLoaded > magsTaken) {
      return { ok: false, error: `You packed ${magsTaken} magazine(s) — ${magsLoaded} can't have come back loaded.` };
    }
  }
  if (roundsLoaded !== null) {
    if (!Number.isInteger(roundsLoaded) || roundsLoaded < 0) {
      return { ok: false, error: 'Rounds still in magazines must be zero or more.' };
    }
    if (magsLoaded === null) {
      return { ok: false, error: 'Say how many magazines came back loaded before recording rounds in them.' };
    }
    if (roundsLoaded > 0 && magsLoaded === 0) {
      return { ok: false, error: 'No magazines came back loaded, so there are no rounds left in them.' };
    }
  }
  return { ok: true, error: '' };
}

export interface MagReturnsLine {
  mags_taken: number;
  mags_returned_loaded: number | null;
  mags_returned_empty: number | null;
  rounds_returned_loaded: number | null;
}

/**
 * The done-state's returns sentence.
 *
 * OV5 — `mags_returned_empty IS NULL` is the discriminator for a line written
 * before M77c. Those rows carry a computed ceil-guess in
 * `mags_returned_loaded` that no UI ever read; rendering it now would present
 * arithmetic as something the user observed. They say so instead.
 *
 * X is always phrased as derived ("the other 2 empty"), never as an
 * observation, because it is `mags_taken - Y` and nobody counted it.
 */
export function magReturnsSummary(line: MagReturnsLine): string {
  if (line.mags_returned_empty === null) return 'returns not recorded';

  const y = line.mags_returned_loaded ?? 0;
  const x = line.mags_returned_empty;
  const z = line.rounds_returned_loaded;

  if (y === 0) return x === 1 ? 'came back empty' : `all ${x} came back empty`;

  const loadedPart = z === null
    ? `came back ${y} loaded · rounds unknown`
    : `came back ${y} loaded (${z.toLocaleString()} rds)`;
  if (x === 0) return loadedPart;
  return `${loadedPart} · the other ${x} empty`;
}

export interface MagFit {
  firearm_id: string | null;
  build_id?: string | null;
}

/**
 * D3 — default the pack dialog's "Feeds" picker when the answer is unambiguous:
 * exactly one firearm fits this group AND that firearm is on this session.
 *
 * ── M82d REWRITES THIS RATIONALE ────────────────────────────────────────────
 *
 * It used to read: "A BUILD is never defaulted. A guessed build writes into
 * `v_build_round_count`, and a wrong build there is a lifetime number nobody
 * will think to audit."
 *
 * That was right about the danger and wrong about this case, and the difference
 * is CHOSEN-AT-ATTACH vs GUESSED. The build returned here is not inferred from
 * anything — it is the configuration the owner already picked when they added
 * that firearm to this session. Declining to carry it did not avoid a guess; it
 * threw away an answer, and left the magazine's rounds attributed to a gun but
 * to no build, which is its own quiet wrongness in the same lifetime number.
 *
 * The guess-avoidance still holds where it belongs: the FIREARM is defaulted
 * only when exactly one fits, and the caller renders the result through
 * `configurationLabel` so the value being defaulted is visible before saving.
 */
export function defaultMagAttribution(
  fits: MagFit[],
  /** The session's attached rows — M82d carries each one's chosen build. */
  sessionFirearms: { firearm_id: string; build_id?: string | null }[],
): { firearmId: string | null; buildId: string | null } {
  const ids = [...new Set(fits.map((f) => f.firearm_id).filter((id): id is string => !!id))];
  if (ids.length !== 1) return { firearmId: null, buildId: null };
  const match = sessionFirearms.find((sf) => sf.firearm_id === ids[0]);
  if (!match) return { firearmId: null, buildId: null };
  return { firearmId: match.firearm_id, buildId: match.build_id ?? null };
}
